import type zh from '../zh/cpa';

const en: typeof zh = {
  title: 'CPA',
  tabTree: 'Decision tree',
  tabPrograms: 'Program parameters',
  tabRules: 'Rules',
  tabStacking: 'Stacking',
  tabVersions: 'Versions',

  treeTitle: 'Decision tree',
  viewCanvas: 'Canvas',
  viewOutline: 'Outline',
  edit: 'Edit',
  cancel: 'Cancel',
  save: 'Save',

  draftBannerTitle: 'The draft lives only in this browser tab',
  draftBannerBody:
    'The service has no API that writes a questionnaire definition (the five JSON files are read off the Lambda classpath), so Save is unavailable and Cancel or a refresh discards everything. Edits are still useful for seeing what a rule change would do.',

  paletteQuestion: '＋ Question',
  kindSingle: 'Single select',
  kindSingleHint: 'One answer from a list',
  kindMulti: 'Multi select',
  kindMultiHint: 'Several answers; stored as an array, matched with .includes()',
  kindNumber: 'Number',
  kindNumberHint: 'Numeric input, gets dataType: NUMBER',
  kindAmount: 'Amount',
  kindAmountHint: 'A number with the unit $K; the unit is display only',
  kindPercent: 'Percentage',
  kindPercentHint: 'A number with unit % and scale 0.01 — 30 is stored as 0.3, so rules say >= 0.3',
  kindSfdc: 'SFDC account',
  kindSfdcHint: 'Its own component: your accounts, or a typed 18-char id validated remotely',
  kindText: 'Text',
  kindTextHint: 'Free text, no options',
  paletteSubtree: '⊞ Subtree',
  paletteRule: '+ Rule',
  paletteDelete: '🗑 Delete',
  paletteInsert: 'Insert on edge',
  paletteUndo: '↩ Undo',
  tidyUp: 'Tidy up',
  tidyUpWithCount: 'Tidy up ({{count}} hand-placed card(s) reset)',

  hintInsert: 'Insert mode: click any connection to splice a new question into it.',

  outlineFilterPlaceholder: 'Filter questions',
  outlineCount: '{{count}} questions',
  outlineEmpty: 'No matching questions',
  outlineColQuestion: 'Question',
  outlineColAnswer: 'Answer',
  outlineCombined: 'combined',
  outlineColLogic: 'Condition',
  outlineColNext: 'Next',
  outlineTerminal: 'Terminal',
  outlineExpand: 'Expand',
  outlineCollapse: 'Collapse',

  delTitle: 'Delete "{{id}}"?',
  delConfirm: 'Delete',
  delRelink: 'Incoming edges will be re-pointed to:',
  delRelinkNone: '(nothing — the upstream node loses its outgoing edge)',
  delBrokenTitle: '{{count}} rule(s) will stop matching',
  delBrokenBody:
    "These rules read this question's answer. Their expressions survive the delete but will read undefined — <strong>the engine raises nothing; the rules simply never match again</strong>.",
  delColNode: 'On node',
  delColKind: 'Kind',
  delColExpr: 'Expression',
  delNoBroken: 'No rule reads this question — deleting it changes no recommendation',
  delOrphansTitle: '{{count}} downstream node(s) go with it',
  delOrphansBody: 'Once it is gone these become unreachable, so they are removed too:',

  connTitle: 'Which answer takes this edge?',
  connConfirm: 'Connect',
  connPickLabel: 'Which answer follows this edge?',
  connPickHint: 'Only answers no other conditional edge has claimed.',
  connPickPlaceholder: 'Pick an answer',
  connExprLabel: 'Condition expression',
  connExprHint: 'This question has no answer options (a numeric input, for example).',
  rewriteTitle: 'The existing unconditional edge becomes the default branch',
  rewriteBody:
    'The engine stops looking at conditional edges as soon as it sees <code>nextNodeId</code>, so it is rewritten as a trailing <code>true</code> branch — <strong>answers that do not match the new condition behave exactly as before.</strong>',

  insTitle: 'Insert a question on this edge?',
  insConfirm: 'Insert',
  insBody: ', with the new node in between.',
  insNote:
    'The new node has only Yes / No options and no rule reads it — it gets asked, but changes no recommendation yet.',

  brTitle: 'New branch from "{{id}}"',
  brConfirm: 'Add',
  brPickLabel: 'Which answer takes this new branch?',
  brPickHint: 'Only answers no conditional edge has claimed.',
  brExprLabel: 'Branch condition',
  brExprHint: 'This question has no option values to offer (a numeric input, for example).',
  brRewriteTitle: 'This node has an unconditional edge today; it becomes a conditional one',
  brRewriteBody:
    'The existing edge becomes a trailing <code>true</code> default branch. <strong>Every answer that does not match the new condition behaves exactly as it does now.</strong>',
  brNewNodeNote:
    'The new node is a terminal node by default, with Yes / No options and no rules.',

  todoSubtreeTitle: 'Subtree node',
  todoHeader: 'This button has the look but no behaviour yet',
  todoSubtreeBody:
    'The mock has a "subtree" node; the stored model has none — the five questionnaires are independent, with no way to reference a shared subtree. The service needs a referenceable subtree entity before this button can do anything.',
  ok: 'Got it',

  programsCaption: 'From the investment program table · ratios in % per year (one-time programs are spread across the support years) · amounts in $',
  programsEdit: 'Edit',
  programsCreate: 'Create new',
  programsSearch: 'Find programs, POC, SLA',
  programsMatches: '{{count}} matches',
  programsLoading: 'Loading program parameters',
  programsEmpty: 'No programs',
  programsNoMatch: 'No matching programs',
  programsClearFilter: 'Clear filter',
  programsLoadError: 'Could not load program parameters',
  programsDenied: 'Only Deal Desk admins (the admin group) can view program parameters.',
  programsRetry: 'Retry',
  colPriority: 'Priority',
  colName: 'Program',
  colType: 'Type',
  colCycle: 'Cycle',
  colRatio: 'Discount',
  colAmount: 'Amount',
  colPoc: 'POC',
  colSla: 'SLA',
  colQualifier: 'ARR threshold',
  colExclusive: 'Mutually exclusive',
  colLinks: 'Links',
  colUpdated: 'Updated',
  ppaNonPpa: 'Standalone',
  ppaSceLedCrossService: 'SCE-led cross service PPA',
  ppaSellerLedCrossService: 'Seller-led cross service PPA',
  ppaSceLedServiceSpecific: 'SCE-led service specific PPA',
  ppaSellerLedServiceSpecific: 'Seller-led service specific PPA',
  ppaRPpa: 'R-PPA',
  cycleRecurring: 'Yearly',
  cycleOneTime: 'One-time',
  noLimit: 'No limit',
  tiered: 'Tiered',
  linkWiki: 'Wiki',
  linkApply: 'Apply',
  prefsTitle: 'Preferences',
  prefsPageSize: 'Rows per page',
  prefsColumns: 'Visible columns',
  prefsConfirm: 'Confirm',

  stackingCaption: "From each program's mutually_exclusive lists, per region (global / China); a pair is exclusive when either side lists the other, as in the recommendation engine",
  stackingSearch: 'Find programs',
  stackingRelationFilter: 'Relation',
  stackingRelationFilterPlaceholder: 'All relations',
  stackingNoMatch: 'No matching programs',
  stackingStackable: 'Stackable',
  stackingExclusive: 'Exclusive',
  stackingChinaOnly: 'Stackable in China only',
  stackingGlobalOnly: 'Stackable in global only',
  stackingSelf: 'Same program',
  stackingPairs: '{{count}} pairs',
  stackingListedBy: '{{region}}: listed as exclusive by {{names}}',
  stackingRegionGlobal: 'global',
  stackingRegionChina: 'China',

  rulesCaption: 'How the recommendation engine turns candidate programs into plans (questionnaire eligibility rules are in the decision tree)',
  rulesVersion: 'Rules v{{version}}',
  rulesPublished: 'Published {{date}} by {{by}}',
  rulesEngineImplemented: 'Implemented in the engine · {{ref}}',
  rulesEnginePending: 'Not yet implemented in the engine',
  rulesLoadError: 'Could not load the rules',
  rulesFilter: 'Filter by program',
  rulesAllPrograms: 'All programs',
  rulesNoMatch: 'No rule mentions this program',
  rulesLegendLive: 'Live: from program parameters',
  rulesLegendEngine: 'Engine constant',
  rulesParamLive: 'From program parameters: {{source}}',

  editorCreateTitle: 'New program',
  editorEditTitle: 'Edit: {{name}}',
  editorBasics: 'Basics',
  editorNameFixed: 'The name cannot change after saving: questionnaires, the engine and exclusion lists refer to programs by name',
  editorNameLocked: 'The name cannot change',
  editorPriorityHint: 'Lower comes first',
  editorDiscount: 'Discount',
  discountRatio: 'Ratio',
  discountAmount: 'Amount',
  discountTable: 'Tier table',
  editorMinRatio: 'Minimum ratio (%)',
  editorMaxRatio: 'Maximum ratio (%)',
  editorMinAmount: 'Minimum amount ($)',
  editorMaxAmount: 'Maximum amount ($)',
  editorTiers: 'Tiers',
  editorTiersHint: 'ARR band ($, lower bound included, upper excluded) and the ratio per year, comma separated: 3, 4, 5 = 3% / 4% / 5% in years 1 / 2 / 3',
  editorTierMin: 'ARR from',
  editorTierMax: 'ARR to',
  editorTierRatios: 'Ratio per year (%)',
  editorAddTier: 'Add tier',
  editorRemoveTier: 'Remove this tier',
  editorEligibility: 'Threshold and exclusions',
  editorQualifierMin: 'Threshold from ($, optional)',
  editorQualifierMax: 'Threshold to ($, optional)',
  editorExclusiveGlobal: 'Mutually exclusive (global)',
  editorExclusiveChina: 'Mutually exclusive (China)',
  editorExclusiveHint: 'The engine checks both ways: listing it on one side is enough',
  editorContacts: 'Contacts and links',
  editorOnePerLine: 'One per line',
  editorReview: 'Review changes',
  editorBack: 'Back',
  editorConfirmSave: 'Confirm and save',
  editorLiveWarning: 'Takes effect at once: the next recommendation uses the new settings.',
  editorLiveWarningNew: 'The program appears in program parameters and stacking at once, but is only recommended once a questionnaire rule names it (an SDE change).',
  editorNoChanges: 'Nothing changed.',
  editorField: 'Field',
  editorBefore: 'Before',
  editorAfter: 'After',
  editorSaved: 'Saved: {{name}}',
  diff_name: 'Name',
  diff_priority: 'Priority',
  diff_ppaType: 'Type',
  diff_discountCycle: 'Cycle',
  diff_discount: 'Discount',
  diff_qualifier: 'Threshold',
  diff_exclusiveGlobal: 'Exclusive (global)',
  diff_exclusiveChina: 'Exclusive (China)',
  diff_contacts: 'POC',
  diff_sla: 'SLA',
  diff_wikis: 'Wiki',
  diff_applicationLinks: 'Application links',
  formRequired: 'Required',
  formPriority: 'Enter a whole number, 0 or more',
  formNumber: 'Enter a number',
  formRatioRange: 'Needs 0 ≤ minimum ≤ maximum ≤ 100',
  formMinMax: 'The minimum cannot exceed the maximum',
  formTiersEmpty: 'Add at least one tier',
  formTier: 'Every tier needs from < to and at least one yearly ratio',
  formHttps: 'Links must start with https://',
  saveConflict: 'Someone changed this program after you opened it; close, reload and try again',
  saveDisabled: 'Editing program parameters is not open in this stage yet',
  saveInvalid: 'The service rejected the settings',
  saveFailed: 'Could not save',

  versionsNewDraft: 'New draft',
  versionsConfirm: 'Confirm',
  versionsReason: 'Reason',
  versionsReference: 'Implemented in',
  versionsReferenceHint: 'A CR link or a commit, e.g. https://code.amazon.com/reviews/CR-123456',
  versionsConflict: 'This version changed after you opened it; reload and try again',
  versionsOwnPublish: 'You cannot publish your own version; ask another admin',
  versionStatus_draft: 'Draft',
  versionStatus_in_review: 'In review',
  versionStatus_published: 'Published',
  versionStatus_archived: 'Archived',
  versionAction_submit: 'Submit for approval',
  versionAction_discard: 'Discard draft',
  versionAction_publish: 'Publish',
  versionAction_reject: 'Send back',
  versionAction_implemented: 'Mark implemented in the engine',
  versionConfirm_submit: 'Once submitted the draft cannot be edited until another admin publishes or sends it back.',
  versionConfirm_discard: 'The draft will be deleted; this cannot be undone.',
  versionConfirm_publish: 'This version becomes the current rules and the previous published version is archived. The engine does not change by itself; an SDE implements it and marks it.',
  versionConfirm_reject: 'The version goes back to draft so its author can change and resubmit it.',
  versionConfirm_implemented: 'Confirm that the engine code implements the rules of this version.',
  rulesViewingVersion: 'Viewing v{{version}} ({{status}}), not the rules in force.',
  rulesVersionPick: 'Version',
  rulesGoToOpen: 'Go to v{{version}} (open)',
  rulesBackToCurrent: 'View current rules',

  editRulesStart: 'Edit draft',
  editRulesTitle: 'Editing v{{version}} (draft)',
  editRulesCaption: 'Save when done. Saving changes the draft only; it becomes the rules in force once submitted and published, and an SDE implements it in the engine.',
  editRulesSave: 'Save draft',
  editRulesNote: 'What changed',
  editRulesNoteHint: 'Shown as the version note for the approver',
  editRulesFix: 'Some fields need fixing; see the ones marked in red',
  editRuleSectionTitle: 'Section title',
  editRuleSectionSummary: 'Section summary',
  editRuleUntitled: '(untitled section)',
  editRuleRemoveSection: 'Remove this section',
  editRuleTextHint: 'Refer to a parameter below as {name}; names are letters and digits only',
  editRuleRemove: 'Remove this rule',
  editRuleParamNameLabel: 'Name',
  editRuleParamKind: 'Source',
  editRuleKind_engine: 'Fixed value',
  editRuleKind_program: 'From program parameters',
  editRuleParamValue: 'Value, e.g. $300K, 3 years',
  editRuleParamProgram: 'Program',
  editRuleParamField: 'Field',
  editRuleField_qualifier_min: 'Threshold from',
  editRuleField_qualifier_max: 'Threshold to',
  editRuleRemoveParam: 'Remove parameter',
  editRuleAddParam: 'Add parameter',
  editRuleProgramsLoading: 'Loading programs',
  editRuleProgramsError: 'Could not load the programs, so the program pickers are empty for now',
  editRulePrograms: 'Programs',
  editRuleAdd: 'Add rule',
  editRuleAddSection: 'Add section',
  editRuleTable: 'Table',
  editRuleRemoveRow: 'Remove this row',
  editRuleAddRow: 'Add row',
  editRuleTitleRequired: 'The section needs a title',
  editRuleTextRequired: 'The rule needs text',
  editRuleParamName: 'Parameter names are letters and digits only',
  editRuleParamDuplicate: 'Two parameters have the same name',
  editRulePlaceholder: 'The text refers to a parameter that does not exist',
  editRuleParamMissing: 'Each parameter needs a value or a program',
};

export default en;
